import React, { useEffect, useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';

type BlockRow = {
  id: string;
  email: string | null;
  user_id: string | null;
  section: 'lobby' | 'forum' | 'all';
  reason: string | null;
  created_at: string;
};

export default function AccessControl() {
  const { profile } = useProfile();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [section, setSection] = useState<'lobby' | 'forum' | 'all'>('all');
  const [reason, setReason] = useState('');
  const [blocks, setBlocks] = useState<BlockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = (profile?.role || '').toLowerCase() === 'admin';

  const fetchBlocks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('access_blocks')
      .select('id,email,user_id,section,reason,created_at')
      .order('created_at', { ascending: false });
    if (error) {
      toast({ title: 'Error', description: 'Failed to load blocks', variant: 'destructive' });
    } else {
      setBlocks((data || []) as BlockRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchBlocks();
  }, [isAdmin]);

  const addBlock = async () => {
    if (!email.trim()) {
      toast({ title: 'Email required', description: 'Enter an email to block (user may not have signed up yet).' });
      return;
    }
    const { error } = await supabase
      .from('access_blocks')
      .insert({
        email: email.trim(),
        section,
        reason: reason.trim() || null,
      });
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Blocked', description: `${email} is blocked from ${section}.` });
    setEmail('');
    setReason('');
    fetchBlocks();
  };

  const removeBlock = async (id: string) => {
    const { error } = await supabase.from('access_blocks').delete().eq('id', id);
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Removed', description: 'Block removed.' });
    setBlocks(prev => prev.filter(b => b.id !== id));
  };

  if (!isAdmin) {
    return (
      <MainLayout>
        <div className="p-6">
          <Card>
            <CardHeader>
              <CardTitle>Access Control</CardTitle>
              <CardDescription>Only administrators can manage blocks.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">You do not have permission to view this page.</p>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Block User Access</CardTitle>
            <CardDescription>Add an email to block access to Lobby, Forums, or both. Works even if the user hasn’t signed up.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-3">
                <Input
                  type="email"
                  placeholder="user@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <Select value={section} onValueChange={(v) => setSection(v as any)}>
                  <SelectTrigger><SelectValue placeholder="Section" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="lobby">Lobby</SelectItem>
                    <SelectItem value="forum">Forum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Button className="w-full" onClick={addBlock}>Block</Button>
              </div>
            </div>
            <Input
              placeholder="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Current Blocks</CardTitle>
            <CardDescription>{loading ? 'Loading...' : `${blocks.length} entries`}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Section</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[1%]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blocks.map(b => (
                    <TableRow key={b.id}>
                      <TableCell>{b.email || '—'}</TableCell>
                      <TableCell className="capitalize">{b.section}</TableCell>
                      <TableCell>{b.reason || '—'}</TableCell>
                      <TableCell>{new Date(b.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm" onClick={() => removeBlock(b.id)}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {blocks.length === 0 && !loading && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                        No blocked users yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}